/**
 * Two-column key/value list. Key on the left in dim text, value on the
 * right in bright text (or full-width below for very long values).
 */

import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { OcppTheme } from './OcppTheme';

export interface OcppKvItem {
  key: string;
  value: React.ReactNode;
  /** When true the value renders below the key on its own line. */
  fullWidth?: boolean;
}

const isLongValue = (v: React.ReactNode): boolean => {
  return typeof v === 'string' && v.length > 60;
};

const OcppKvList: React.FC<{ items: OcppKvItem[] }> = ({ items }) => {
  return (
    <View>
      {items.map((item, idx) => {
        const stacked = !!item.fullWidth || isLongValue(item.value);
        return (
          <View key={`${item.key}-${idx}`} style={[styles.row, stacked && styles.stacked]}>
            <Text style={styles.key} numberOfLines={1}>
              {item.key}
            </Text>
            {typeof item.value === 'string' ? (
              <Text
                style={[styles.value, stacked && styles.valueStacked]}
                selectable
              >
                {item.value || '—'}
              </Text>
            ) : (
              <View style={[styles.valueNode, stacked && styles.valueStacked]}>{item.value}</View>
            )}
          </View>
        );
      })}
    </View>
  );
};

export default OcppKvList;

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 6,
    gap: 12,
  },
  stacked: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 4,
  },
  key: {
    color: OcppTheme.textMuted,
    fontSize: 13,
    flexShrink: 0,
    minWidth: 110,
  },
  value: {
    color: OcppTheme.textPrimary,
    fontSize: 13,
    flex: 1,
    textAlign: 'right',
  },
  valueNode: {
    flex: 1,
    alignItems: 'flex-end',
  },
  valueStacked: {
    textAlign: 'left',
    alignItems: 'stretch',
  },
});
